<template>
  <MobileNavigation v-if="isSmallScreen" :user="user" :items="menuItems" />
  <Header v-else :user="user" :items="menuItems" />
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { useAccountStore } from "@/stores/account";
import { useApiStore } from "@/stores/api";
import Header from "@/components/Header.vue";
import MobileNavigation from "@/components/MobileNavigation.vue";
import { useGrid } from "vue-screen";
import dashboardIcon from "@icon/dashboard.svg";
import newProductIcon from "@icon/new-product.svg";
import newWorkflowIcon from "@icon/new-workflow.svg";
import newOrderAcceptanceTemplateIcon from "@icon/new-order-acceptance-template.svg";
import productsIcon from "@icon/products.svg";
import productIssuanceIcon from "@icon/product-issuance.svg";
import workflowsIcon from "@icon/workflows.svg";
import orderAcceptanceTemplatesIcon from "@icon/order-acceptance-templates.svg";
import couponsIcon from "@icon/coupons.svg";
import marketplaceIcon from "@icon/marketplace.svg";
import documentsIcon from "@icon/documents.svg";
import supportIcon from "@icon/support.svg";
import logoutIcon from "@icon/logout.svg";

export default defineComponent({
  name: "Navigation",
  components: { Header, MobileNavigation },
  setup: () => {
    return { accountStore: useAccountStore(), apiStore: useApiStore(), grid: useGrid() };
  },
  data() {
    return {};
  },
  props: {
    user: Object as User,
  },
  computed: {
    isProduction() {
      return import.meta.env.VITE_APP_ENVIRONMENT === "production";
    },
    hasSrfAccess() {
      return this.user?.srfAccess && !this.user?.isUserPayingAgent && !this.user?.isUserIssuer;
    },
    hasSrfLiteAccess() {
      // none, full, restricted, hide when none
      return (
        this.user.isUserGentwo ||
        (this.user?.srfLiteLevelAccess && this.user?.srfLiteLevelAccess !== "None")
      );
    },
    hasAmcForPmsAccess() {
      // none, full - mirrors hasSrfLiteAccess but reads the AMC-for-PMs claim
      return (
        this.user.isUserGentwo ||
        (this.user?.amcForPmsLevelAccess && this.user?.amcForPmsLevelAccess !== "None")
      );
    },
    hasOrderAcceptanceAccess() {
      return (
        (!this.user?.isUserIssuer || this.user?.isUserGentwo) &&
        this.user?.isOrderAcceptancePayingAgent
      );
    },
    menuItems() {
      return [
        ...(!this.user.hasDocumentHubAccess && (this.user.isUserGentwo || this.user.isClient)
          ? [
              {
                label: "Dashboard",
                icon: dashboardIcon,
                to: { name: "Dashboard" },
              },
            ]
          : []),
        // A separate app served under this path on the same host, so a full page load, not a route.
        // The AssetizationUniverse flag gates the feature; the claim says who may use it.
        ...(this.apiStore.options?.assetizationUniverse && this.user.canAccessAssetizationUniverse
          ? [
              {
                label: "Assetization Universe",
                icon: marketplaceIcon,
                url: "/assetization-universe",
              },
            ]
          : []),
        ...(this.user.hasGenTwoProAccess
          ? [
              {
                label: "Create",
                items: [
                  {
                    label: "New Product",
                    icon: newProductIcon,
                    to: { name: "SRF", params: { id: "new" } },
                    visible: () => this.hasSrfAccess,
                  },
                  {
                    label: "New AMC for Banks",
                    icon: newProductIcon,
                    to: { name: "SRFLite", params: { id: "new" } },
                    visible: () => this.hasSrfLiteAccess,
                  },
                  {
                    label: "New Product - Lite",
                    icon: newProductIcon,
                    to: { name: "BankableAmc", params: { id: "new" } },
                    isNewFeature: true,
                    visible: () => this.hasAmcForPmsAccess,
                  },
                  {
                    label: "New Workflow",
                    icon: newWorkflowIcon,
                    to: { name: "NewWorkflow" },
                  },
                  {
                    label: "New Order Acceptance Template",
                    icon: newOrderAcceptanceTemplateIcon,
                    to: { name: "OrderAcceptanceTemplate", params: { action: "new" } },
                    visible: () => this.hasOrderAcceptanceAccess,
                  },
                ],
              },
            ]
          : []),
        ...(this.user.hasGenTwoProAccess
          ? [
              {
                label: "Manage",
                items: [
                  {
                    label: "Products",
                    icon: productsIcon,
                    to: { name: "Products" },
                  },
                  {
                    label: "Product Issuance",
                    icon: productIssuanceIcon,
                    to: { name: "SRFRequests" },
                    visible: () => this.hasSrfAccess,
                  },
                  {
                    label: "Workflows",
                    icon: workflowsIcon,
                    to: { name: "Workflows", params: { type: "all", status: "pending" } },
                  },
                  {
                    label: "Order Acceptance Templates",
                    icon: orderAcceptanceTemplatesIcon,
                    to: { name: "OrderAcceptanceTemplates" },
                    visible: () => !this.user?.isUserIssuer || this.user?.isUserGentwo,
                  },
                  {
                    label: "Planned Coupons",
                    icon: couponsIcon,
                    to: { name: "PlannedCoupons" },
                    visible: () => this.user?.isUserPayingAgent || this.user?.isUserGentwo,
                  },
                ],
              },
            ]
          : []),

        {
          label: "Find Information",
          items: [
            {
              label: "Document Hub",
              icon: documentsIcon,
              to: { name: "DocumentHub" },
              ...(this.isSmallScreen && {
                items: [
                  {
                    label: "Gentwo Documents",
                    icon: supportIcon,
                    to: { name: "NonDigitalDocuments" },
                  },
                ],
              }),
            },
            ...(this.user.hasGenTwoProAccess
              ? [
                  {
                    label: "Help Center",
                    icon: supportIcon,
                    to: { name: "Support" },
                  },
                ]
              : []),
          ],
        },
        ...(this.isSmallScreen
          ? [
              {
                label: "Log Out",
                icon: logoutIcon,
                command: async () => {
                  this.$router.push({ name: "Logout" });
                },
              },
            ]
          : []),
      ];
    },
    isSmallScreen() {
      return !this.grid["md"];
    },
  },
});
</script>
